import { zodResolver } from '@hookform/resolvers/zod';
import { AppContext } from 'context/AppContextProvider';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useCreateSchoolClassAsync, useDeleteSchoolClassAsync, useUpdateSchoolClassAsync } from 'services/SchoolClass/hooks/useMutation';
import { SCHOOL_CLASS_KEYS, useGetAllSchoolClassByLicenseQuery, useGetClassDashboardQuery, useGetClassNotificationStatsQuery, useGetCriticalStudentsQuery, useGetGlobalDashboardQuery, useGetLiveAttendanceQuery, useGetSchoolClassByIdQuery, useGetSchoolClassCountQuery } from 'services/SchoolClass/hooks/useQuery';
import { useLiveAttendanceSocket } from 'services/SchoolClass/hooks/useSocket/useLiveAttendanceSocket';
import { SchoolClassFormData, SchoolClassFormSchema } from 'services/SchoolClass/schemas';
import { ISchoolClassBaseProps, ISchoolClassFilterParams, ISchoolClassSidebarProps } from 'services/SchoolClass/types';
import { IPaginationParams } from 'services/types';

const STORAGE_KEY_ID = 'alertai_schoolClass_id';
const STORAGE_KEY_TAB = 'alertai_schoolClass_tab';

const emptyFormDefaults = {
  name: '',
  recognitionsId: [],
  shiftId: null
};

export const useSchoolClass = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useContext(AppContext);
  const { t } = useTranslation();
  
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pagination, setPagination] = useState<IPaginationParams>({ pageNumber: 1, pageSize: 10 });
  const [filter, setFilter] = useState<ISchoolClassFilterParams>({});
  
  const [selectedId, setSelectedIdState] = useState<number | 'new' | 'all'>('all');
  const [activeTab, setActiveTabState] = useState<string>('dashboard');

  const setSelectedId = useCallback((id: number | 'new' | 'all') => {
    sessionStorage.setItem(STORAGE_KEY_ID, String(id));
    setSelectedIdState(id);
  }, []);

  const setActiveTab = useCallback((tab: string) => {
    sessionStorage.setItem(STORAGE_KEY_TAB, tab);
    setActiveTabState(tab);
  }, []);

  useEffect(() => {
    const savedId = sessionStorage.getItem(STORAGE_KEY_ID);
    if (savedId) {
      if (savedId === 'new' || savedId === 'all') setSelectedIdState(savedId);
      else if (!isNaN(Number(savedId))) setSelectedIdState(Number(savedId));
    }

    const savedTab = sessionStorage.getItem(STORAGE_KEY_TAB);
    if (savedTab) {
      setActiveTabState(savedTab);
    }
  }, []);

  const isAttendanceNeeded = (activeTab === 'presenca' || activeTab === 'gestao') && typeof selectedId === 'number';
  const attendanceTargetId = isAttendanceNeeded ? selectedId : null;

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [classToDelete, setClassToDelete] = useState<ISchoolClassSidebarProps | null>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilter((prev) => ({ ...prev, name: searchTerm || undefined }));
      setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const { 
    data: classList = [], 
    isLoading: isLoadingList, 
    isFetching: isFetchingList, 
    refetch 
  } = useGetAllSchoolClassByLicenseQuery(pagination, filter);
  
  const { 
    data: attendanceData, 
    isFetching: isFetchingAttendance 
  } = useGetLiveAttendanceQuery(attendanceTargetId);

  const {
    data: dashboardData,
    isFetching: isFetchingDashboard
  } = useGetClassDashboardQuery(typeof selectedId === 'number' ? selectedId : null);

  useLiveAttendanceSocket({ 
    classId: attendanceTargetId 
  });

  const {
    data: globalDashboardData,
    isFetching: isFetchingGlobalDashboard
  } = useGetGlobalDashboardQuery();

  const { 
    data: notificationStats, 
    isFetching: isLoadingStats 
  } = useGetClassNotificationStatsQuery(typeof selectedId === 'number' ? selectedId : null);

  const { 
    data: criticalStudents = [], 
    isFetching: isLoadingCritical 
  } = useGetCriticalStudentsQuery(typeof selectedId === 'number' ? selectedId : null);

  const { 
    data: totalCount = 0, 
    refetch: refetchCount 
  } = useGetSchoolClassCountQuery(filter);
  
  const { 
    data: classData, 
    isFetching: isFetchingDetail 
  } = useGetSchoolClassByIdQuery(
    typeof selectedId === 'number' ? selectedId : null
  );

  const { mutate: createClass, isLoading: isCreating } = useCreateSchoolClassAsync();
  const { mutate: updateClass, isLoading: isUpdating } = useUpdateSchoolClassAsync();
  const { mutateAsync: deleteClass, isLoading: isDeleting } = useDeleteSchoolClassAsync();

  const methods = useForm<SchoolClassFormData>({
    resolver: zodResolver(SchoolClassFormSchema),
    mode: 'onTouched',
    defaultValues: emptyFormDefaults
  });

  const { reset } = methods;

  useEffect(() => {
    if (classData && typeof selectedId === 'number') {
      reset({ 
        name: classData.name, 
        recognitionsId: classData.recognitionsId || [],
        shiftId: classData.shiftId || null 
      });
    }
  }, [classData, selectedId, reset]);

  const handleSelectClass = (id: number | 'new' | 'all') => {
    if (id === selectedId) return;
    
    setSelectedId(id);
    if (id !== 'new') {
      reset(emptyFormDefaults);
    }
  };

  const handleCreateNew = () => {
    setSelectedId('new');
    setActiveTab('gestao');
    reset(emptyFormDefaults);
  };

  const handleOpenDeleteModal = (e: React.MouseEvent, classItem: ISchoolClassSidebarProps) => {
    e.stopPropagation();
    setClassToDelete(classItem);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!classToDelete) return;
    const idToDelete = classToDelete.id;

    setDeleteModalOpen(false);
    if (selectedId === idToDelete) {
      setSelectedId('all'); 
    }

    try {
      await deleteClass(idToDelete);
      showSnackbar('Turma excluída com sucesso.', 'success');

      queryClient.removeQueries({ queryKey: [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.DETAIL, idToDelete] });
      queryClient.invalidateQueries({ queryKey: [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.LIST] });
      queryClient.invalidateQueries({ queryKey: [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.COUNT] });

      if (classList.length === 1 && pagination.pageNumber > 1) {
        setPagination(prev => ({ ...prev, pageNumber: prev.pageNumber - 1 }));
      }
    } catch (error) {
      const err = error as Error;
      showSnackbar(err.message || 'Erro ao excluir turma.', 'error');
      
      if (selectedId === idToDelete) setSelectedId(idToDelete);
    } finally {
      setClassToDelete(null);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, pageNumber: newPage })); 
  };

  const handleRefresh = () => {
    refetch();
    refetchCount();
  };

  const onSave = (formData: SchoolClassFormData) => {
    const payload: ISchoolClassBaseProps = {
      name: formData.name,
      recognitionsId: formData.recognitionsId,
      shiftId: formData.shiftId
    };

    if (selectedId === 'new') {
      createClass(
        { data: payload }, 
        { 
          onSuccess: (newClass) => { 
            showSnackbar('Turma cadastrada com sucesso!', 'success');
            setSelectedId(newClass.id); 
            queryClient.invalidateQueries({ queryKey: [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.LIST] });
            queryClient.invalidateQueries({ queryKey: [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.COUNT] });
          }, 
          onError: (error: unknown) => {
            const err = error as Error;
            showSnackbar(err.message || 'Erro ao criar turma.', 'error');
          }
        }
      );
    } else {
      updateClass(
        { id: Number(selectedId), data: payload }, 
        { 
          onSuccess: () => {
            showSnackbar('Alterações salvas com sucesso!', 'success');
            queryClient.invalidateQueries({ queryKey: [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.LIST] });
            queryClient.invalidateQueries({ queryKey: [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.DETAIL, Number(selectedId)] });
          }, 
          onError: (error: unknown) => {
            const err = error as Error;
            showSnackbar(err.message || 'Erro ao atualizar turma.', 'error');
          }
        }
      );
    }
  };

  return {
    state: {
      activeTab, searchTerm, selectedId, deleteModalOpen, classToDelete, 
      isLoadingList, isFetchingList, isFetchingDetail, isCreating, isUpdating, isDeleting,
      classList, totalCount, pagination, classData, attendanceData, isFetchingAttendance, notificationStats, isLoadingStats, 
      criticalStudents, isLoadingCritical, dashboardData, isFetchingDashboard, globalDashboardData, isFetchingGlobalDashboard
    },
    actions: {
      setActiveTab, setSearchTerm, handleSelectClass, handleCreateNew, handleOpenDeleteModal, 
      handleConfirmDelete, setDeleteModalOpen, onSave,
      handleChangePage, handleRefresh
    },
    form: methods
  };
};